import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonTextInput } from 'components/common/form/text';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { SettingRow } from 'components/common/settings-dialog/row';
import { TeamsContext } from 'contexts/admin/teams.context';
import { IFullDialog } from 'interfaces/i-dialogs';
import { TeamsHelper } from 'lib_ts/classes/teams.helper';
import { MlbAbbreviation } from 'lib_ts/enums/batrack.enums';
import { MLB_TEAMS } from 'lib_ts/enums/mlb.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Competition, COMPETITION_OPTIONS } from 'lib_ts/enums/team.enums';
import { ITeamPermissions } from 'lib_ts/interfaces/i-permissions';
import { ITeam } from 'lib_ts/interfaces/i-team';
import { first } from 'lodash';
import { useContext, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'EditTeamDialog';

interface IProps {
  /** undefined means we're creating a new team */
  id?: string;

  onClose: () => void;
}

export const EditTeamDialog = (props: IProps) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const { adminTeams } = userRoleFeatureFlags;

  const {
    teams,
    loading: loadingTeams,
    create: createTeam,
    update: updateTeam,
  } = useContext(TeamsContext);

  // we never need to update this because the dialog closes after saving
  const [savedName] = useState(teams.find((m) => m._id === props.id)?.name);

  const [team, setTeam] = useState<Partial<ITeam>>(
    () =>
      teams.find((m) => m._id === props.id) ?? {
        name: '',
        suppress_data: false,
      }
  );

  const [domains, setDomains] = useState(team.email_domains?.join('\n') ?? '');

  const [perms, setPerms] = useState<Partial<ITeamPermissions>>(
    team.permissions ?? {}
  );

  const isNew = !team._id;

  const updateMetadata = (model: Partial<ITeam>) => {
    setTeam((prev) => ({ ...prev, ...model }));
  };

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.MD,
    title: isNew ? 'Create Organization' : savedName || 'Update Organization',
    loading: loadingTeams,
    content: (
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <CommonFormGrid columns={2}>
          <CommonSelectInput
            id="team-competition"
            name="competition"
            label="common.competition"
            labelIcon={<SuperAdminIcon />}
            options={COMPETITION_OPTIONS}
            disabled={!adminTeams.update}
            value={team.league}
            onChange={(v) =>
              updateMetadata({
                league: v as Competition,
                abbreviation:
                  v !== Competition.MLB
                    ? MlbAbbreviation.None
                    : team.abbreviation,
              })
            }
            optional
            skipSort
          />

          <CommonSearchInput
            id="team-stats-api-orgId"
            label="MLB Org ID"
            labelIcon={<SuperAdminIcon />}
            options={TeamsHelper.getTeamOptions(Competition.MLB, true)}
            values={
              team.stats_api_orgId ? [team.stats_api_orgId.toString()] : []
            }
            disabled={!adminTeams.update || team.league !== Competition.MLB}
            onChange={(v) => {
              const name = first(v);

              const selectedTeam = MLB_TEAMS.find((t) => t.name === name);

              const abbreviation = Object.values(MlbAbbreviation).find(
                (m) => m === selectedTeam?.code
              ) as MlbAbbreviation;

              updateMetadata({
                name: selectedTeam?.name,
                abbreviation: abbreviation,
                stats_api_orgId: selectedTeam?.teamPk ?? null,
              });
            }}
            hint_md="For Stats API, also changes name and abbreviation"
            optional
          />

          <CommonTextInput
            id="team-name"
            label="Organization"
            labelIcon={<SuperAdminIcon />}
            placeholder="Type a unique value"
            value={team.name}
            disabled={!adminTeams.update}
            onChange={(v) => updateMetadata({ name: v })}
          />

          <CommonSearchInput
            id="team-abbreviation"
            name="abbreviation"
            label="Abbreviation"
            labelIcon={<SuperAdminIcon />}
            options={Object.values(MlbAbbreviation).map((o) => ({
              label: o,
              value: o,
            }))}
            disabled={!adminTeams.update || team.league !== Competition.MLB}
            values={team.abbreviation ? [team.abbreviation] : []}
            hint_md="MLB only"
            onChange={(v) =>
              updateMetadata({
                abbreviation: v[0] as MlbAbbreviation,
              })
            }
            optional
          />

          <CommonSearchInput
            id="team-extra-leagues"
            name="extra-leagues"
            label="Extra Leagues"
            labelIcon={<SuperAdminIcon />}
            options={COMPETITION_OPTIONS}
            disabled={!adminTeams.update}
            values={team.extraLeagues ?? []}
            onChange={(v) =>
              updateMetadata({
                extraLeagues: v as Competition[],
              })
            }
            hint_md="For additional team options in video editor"
            multiple
            optional
          />

          <CommonSelectInput
            id="team-suppress-plate-cal-warnings"
            name="suppress_plate_cal_warnings"
            label="Suppress Plate Calibration Warnings"
            labelIcon={<SuperAdminIcon />}
            options={[
              {
                label: 'True',
                value: 'true',
              },
              {
                label: 'False',
                value: 'false',
              },
            ]}
            disabled={!adminTeams.update}
            value={team.suppress_data?.toString()}
            onBooleanChange={(v) =>
              updateMetadata({
                suppress_data: v,
              })
            }
            hint_md="Suppresses slack notifications for plate calibration warnings"
            optional
          />
          <Box gridColumn="span 2">
            <CommonTextareaInput
              id="team-email-domains"
              label="Email Domains"
              labelIcon={<SuperAdminIcon />}
              placeholder="e.g. domain.com"
              value={domains}
              disabled={!adminTeams.update}
              rows={5}
              onChange={(v) => setDomains(v ?? '')}
              hint_md={[
                `**Please enter only one domain per line.**`,
                'For assigning newly registered users to this team automatically.',
              ].join('\n\n')}
            />
          </Box>
        </CommonFormGrid>

        {adminTeams.permissions && (
          <ErrorBoundary identifier="EditTeamDialogPermissions">
            <Heading size={RADIX.HEADING.SIZE.MD}>
              Permissions <SuperAdminIcon />
            </Heading>

            <Flex direction="column" gap="4">
              <SettingRow
                separatorBefore
                header={`Game Data${HINT_DELIMITER}MLB Stats API`}
                description={
                  <Text>
                    Allow team access to Game Data through MLB's Stats API.
                  </Text>
                }
                input={
                  <Flex justify="end">
                    <CommonSwitchInput
                      id="team-mlb-stats"
                      name="mlb_stats_api"
                      defaultChecked={perms.mlb_stats_api}
                      onCheckedChange={(v) =>
                        setPerms((prev) => ({ ...prev, mlb_stats_api: v }))
                      }
                    />
                  </Flex>
                }
              />

              <SettingRow
                separatorBefore
                header={`Hitters${HINT_DELIMITER}MLB Stats API`}
                description={
                  <Text>
                    Allow team access to team's hitter library through MLB's
                    Stats API.
                  </Text>
                }
                input={
                  <Flex justify="end">
                    <CommonSwitchInput
                      id="team-hitters-import"
                      name="enable_hitters_import"
                      defaultChecked={!!team.enable_hitters_import}
                      onCheckedChange={(v) => {
                        if (v && !team.stats_api_orgId) {
                          NotifyHelper.warning({
                            message_md:
                              'MLB Org ID is required for this feature to work.',
                          });
                        }

                        updateMetadata({
                          enable_hitters_import: v,
                        });
                      }}
                    />
                  </Flex>
                }
              />
            </Flex>
          </ErrorBoundary>
        )}
      </Flex>
    ),
    buttons: [
      {
        label: isNew ? 'Create' : 'Update',
        color: RADIX.COLOR.ACCENT,
        variant: 'solid',
        onClick: async () => {
          if (!team.name) {
            NotifyHelper.error({
              message_md: 'Please enter a team name and try again.',
            });
            return;
          }

          if (
            teams.findIndex(
              (t) => t.name === team.name && t._id !== team._id
            ) !== -1
          ) {
            NotifyHelper.error({
              message_md: 'Please enter a unique team name and try again.',
            });
            return;
          }

          if (!team.league) {
            NotifyHelper.error({
              message_md:
                'Please enter a team league (or select none) and try again.',
            });
            return;
          }

          if (!team.abbreviation) {
            NotifyHelper.error({
              message_md:
                'Please enter a team abbreviation (or select none) and try again.',
            });
            return;
          }

          const payload: Partial<ITeam> = {
            ...team,
            permissions: perms,
          };

          if (domains !== undefined) {
            /** empty string implies no domains, set value to an empty array */
            payload.email_domains = domains
              .split('\n')
              .filter((s) => !!s)
              .map((s) => s.trim());
          }

          if (isNew) {
            await createTeam(payload);
            props.onClose();
            return;
          }

          if (!team._id) {
            // shouldn't trigger
            return;
          }

          await updateTeam({
            ...payload,
            _id: team._id,
          });
          props.onClose();
        },
      },
    ],
    onClose: () => props.onClose(),
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};
